import {useMemo} from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import NewStatusPicker from 'crqlar-react-common/dist/components/NewStatusPicker';
import {useTranslation} from 'react-i18next';

import {
    IfConditionGroup,
    branchSteps,
    buildFieldOptions,
    countIncompleteRules,
    countRules,
    formatSteps,
    readConditionGroups,
    writeConditionGroups
} from '../../../config/ifConditions';
import {buildAvailableVariables} from '../../../config/nodeVariables';
import AutomationTextField from '../variables/AutomationTextField';
import NodeEditorLayout, {NodeEditorState} from '../NodeEditorLayout';
import {NodeEditorV2Props} from '../types';
import IfConditionRules from './IfConditionRules';

type ConditionMode = 'STANDARD' | 'AI';

/**
 * The If step: two outputs, so the summary says where each goes. Rules live in `IfConditionRules` and
 * `config/ifConditions.ts` (which folds a legacy flat `conditions` array into boxes); AI mode asks a yes/no prompt.
 */
const IfConditionEditorV2 = ({
    nodeId,
    nodeConfig,
    setNodeConfig,
    upstreamNodes = [],
    flowNodes = [],
    flowConnections = [],
    readOnly
}: NodeEditorV2Props) => {
    const {t} = useTranslation();

    const mode: ConditionMode = nodeConfig.conditionMode === 'AI' ? 'AI' : 'STANDARD';
    const groups = useMemo(() => readConditionGroups(nodeConfig), [nodeConfig]);
    const fieldOptions = useMemo(() => buildFieldOptions(upstreamNodes, t), [upstreamNodes, t]);

    const branches = useMemo(
        () => branchSteps(nodeId, flowNodes, flowConnections),
        [nodeId, flowNodes, flowConnections]
    );

    const promptVariables = useMemo(() => buildAvailableVariables(upstreamNodes), [upstreamNodes]);

    const ruleCount = groups.reduce((total, group) => total + countRules(group), 0);
    const incomplete = groups.reduce((total, group) => total + countIncompleteRules(group), 0);
    const prompt: string = nodeConfig.conditionPrompt || '';

    const setGroups = (nextGroups: IfConditionGroup[]) =>
        setNodeConfig({...nodeConfig, ...writeConditionGroups(nextGroups)});

    const yesClause = branches.yes.length
        ? t('A matching run goes down yes, to {{steps}}.', {steps: formatSteps(branches.yes, t)})
        : t('Nothing is connected to yes, so a matching run ends here.');

    const noClause = branches.no.length
        ? t('Everything else goes down no, to {{steps}}.', {steps: formatSteps(branches.no, t)})
        : t('Nothing is connected to no, so every other run ends here.');

    const branchesIncomplete = branches.yes.length === 0 || branches.no.length === 0;

    const incompleteClause = incomplete === 0
        ? ''
        : incomplete === 1
            ? t('One rule has no field or operator yet and is ignored.')
            : t('{{count}} rules have no field or operator yet and are ignored.', {count: incomplete});

    const {state, summary}: {state: NodeEditorState; summary: string} = (() => {
        if (mode === 'AI') {
            if (!prompt.trim()) {
                return {
                    state: 'wontRun',
                    summary: t('No prompt yet, so nothing can be decided — every run takes the no branch.')
                };
            }

            return {
                state: branchesIncomplete ? 'caution' : 'configured',
                summary: [t('Every run is judged against your prompt.'), yesClause, noClause].join(' ')
            };
        }

        if (ruleCount === 0) {
            return {
                state: 'wontRun',
                summary: t('No rules yet, so nothing can match — every run takes the no branch.')
            };
        }

        if (incomplete === ruleCount) {
            return {
                state: 'wontRun',
                summary: t('No rule is finished yet, so nothing can match — every run takes the no branch.')
            };
        }

        return {
            state: (incomplete > 0 || branchesIncomplete) ? 'caution' : 'configured',
            summary: [incompleteClause, yesClause, noClause].filter(Boolean).join(' ')
        };
    })();

    return (
        <NodeEditorLayout state={state} summary={summary}>
            <Stack gap={1}>
                <Typography variant={'body2'} color={'text.secondary'}>
                    {t('How the branch is decided')}
                </Typography>

                <NewStatusPicker<ConditionMode>
                    value={mode}
                    disabled={readOnly}
                    ariaLabel={t('How the branch is decided')}
                    onChange={next => setNodeConfig({...nodeConfig, conditionMode: next})}
                    options={[
                        {value: 'STANDARD', label: t('Rules')},
                        {value: 'AI', label: t('AI prompt')}
                    ]}
                />
            </Stack>

            {mode === 'STANDARD' ? (
                <IfConditionRules
                    groups={groups}
                    setGroups={setGroups}
                    fieldOptions={fieldOptions}
                    readOnly={readOnly}
                />
            ) : (
                <AutomationTextField
                    insertion={'step-value'}
                    size={'medium'}
                    disabled={readOnly}
                    label={t('Prompt')}
                    value={prompt}
                    onChange={value => setNodeConfig({...nodeConfig, conditionPrompt: value})}
                    multiline
                    rows={6}
                    placeholder={t('Ask something answerable with yes or no, for example “Did the guest say they were unhappy with the room?”')}
                    helperText={t('Type / to insert a value from an earlier step.')}
                    variables={promptVariables}
                />
            )}
        </NodeEditorLayout>
    );
};

export default IfConditionEditorV2;
